"use client";

import { useEffect } from "react";

import { captureEvent, getDistinctId } from "@/lib/analytics";
import { withCheckoutReference } from "@/lib/purchase";

/**
 * Landing PostHog click instrumentation — a single delegated `document` click
 * listener (mirrors `FacebookPixelCheckoutClicks`). The pricing cards and
 * CTAs are CMS/server-rendered, so rather than thread an `onClick` through
 * every variant we catch every anchor click site-wide and branch on where it
 * leads / where it lives.
 *
 * For Polar checkout links we ALSO rewrite the href in place — appending the
 * anonymous PostHog distinct_id as Polar's `reference_id` — before the browser
 * follows the link, giving closed-loop attribution from landing → order.
 *
 * `captureEvent` no-ops if PostHog hasn't loaded / the user opted out, so this
 * stays in lock-step with consent without any extra gating here.
 */
export function PostHogClicks() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const linkText = anchor.textContent?.trim().slice(0, 80) ?? null;

      // 1) Checkout — link to a Polar hosted checkout.
      let isCheckout = false;
      try {
        isCheckout = new URL(anchor.href).hostname === "buy.polar.sh";
      } catch {
        isCheckout = false;
      }
      if (isCheckout) {
        // Rewrite the href in place FIRST so the browser reads the
        // reference_id-tagged URL after this bubble handler returns.
        const did = getDistinctId();
        if (did) anchor.href = withCheckoutReference(anchor.href, did);

        captureEvent("checkout_click", {
          plan:
            anchor
              .closest("[data-ph-price-card]")
              ?.getAttribute("data-ph-plan") ?? null,
          location: anchor.closest("header")
            ? "nav"
            : anchor.closest("[data-ph-price-card]")
              ? "card"
              : anchor.closest("section[id]")?.id ?? "section",
          link_text: linkText,
          polar_product_path: new URL(anchor.href).pathname,
        });
        return;
      }

      // 2) Nav — any anchor inside the header.
      if (anchor.closest("header")) {
        captureEvent("nav_link_clicked", {
          href: anchor.href,
          link_text: linkText,
          is_cta: anchor.matches('[data-slot="button"], button, .button'),
        });
        return;
      }

      // 3) Footer — any anchor inside the footer.
      if (anchor.closest("footer")) {
        captureEvent("footer_link_clicked", {
          href: anchor.href,
          link_text: linkText,
        });
        return;
      }

      // 4) CTA — links into the /download funnel (the page, or the DMG
      // itself at /dl/Auto-Screenshot.dmg).
      try {
        const url = new URL(anchor.href);
        if (url.pathname.startsWith("/download") || url.pathname.startsWith("/dl/")) {
          captureEvent("cta_clicked", {
            href: anchor.href,
            link_text: linkText,
            destination: "download",
            location: anchor.closest("section[id]")?.id ?? null,
          });
        }
      } catch {
        // Relative / invalid href — nothing to attribute.
      }
    };

    document.addEventListener("click", onClick, false);
    return () => document.removeEventListener("click", onClick, false);
  }, []);

  return null;
}

export default PostHogClicks;
